import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import {
  V2_PAYMENT_CARDS_PATH,
  V2_PAYMENT_SETUP_CONFIG_PATH,
} from '~entities/payment/api/v2-payment-card-paths';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface ParentPaymentMethodSetupResponse {
  clientSecret: string;
  stripePublicApiKey: string;
  stripeAccount?: string;
  linkDisabled?: boolean;
}

export interface ParentPaymentMethodFinalizeResponse {
  success: true;
  paymentMethod: {
    id: string;
    type: string;
    brand?: string;
    last4?: string;
  };
}

export const postParentPaymentMethodSetup = async (parentId: string) => {
  const { data } = await baseAPI.post<ParentPaymentMethodSetupResponse>(
    '/payment-processor/parent-payment-method-setup',
    { parentId },
  );
  return data;
};

export interface V2ParentPaymentMethodSetupResponse {
  provider: string;
  publishableKey?: string;
  clientSecret?: string;
  connectedAccountId?: string;
  linkDisabled?: boolean;
}

export const getV2ParentPaymentMethodSetup = async (parentId: string) => {
  const { data } = await baseAPI.get<V2ParentPaymentMethodSetupResponse>(
    V2_PAYMENT_SETUP_CONFIG_PATH,
    { params: { parentId } },
  );
  return data;
};

export const postV2ParentPaymentMethod = async (body: {
  parentId: string;
  clientSetupId: string;
}) => {
  const { data } = await baseAPI.post(V2_PAYMENT_CARDS_PATH, body);
  return data;
};

export const postParentPaymentMethodSetupFinalize = async (body: {
  parentId: string;
  setupIntentId: string;
}) => {
  const { data } = await baseAPI.post<ParentPaymentMethodFinalizeResponse>(
    '/payment-processor/parent-payment-method-setup/finalize',
    body,
  );
  return data;
};

export const parentPaymentMethodSetupQueryKey = (parentId: string) =>
  [KEYS.PARENT_PAYMENT_METHOD_SETUP, parentId] as const;

/** Drop cached SetupIntent so the next open always creates a fresh one. */
export const removeParentPaymentMethodSetupQuery = (parentId: string) => {
  void queryClient.removeQueries({
    queryKey: parentPaymentMethodSetupQueryKey(parentId),
  });
};

export const useParentPaymentMethodSetupQuery = (
  parentId: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: parentPaymentMethodSetupQueryKey(parentId),
    queryFn: () => postParentPaymentMethodSetup(parentId),
    enabled: enabled && parentId.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

export const useParentPaymentMethodSetupFinalize = () => {
  return useMutation({
    mutationFn: postParentPaymentMethodSetupFinalize,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEYS.CARD] });
    },
  });
};
